import { useEffect, useMemo, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Stack, Plus, PencilSimple, Trash, MagnifyingGlass, FloppyDisk, GearSix, Warning } from "@phosphor-icons/react";
import { CATEGORY_LABELS } from "./CaveatLibraryPicker";
import { NoticeEditor, EMPTY_NOTICE } from "../NoticeEditor";

const label = (c) => CATEGORY_LABELS[c] || c;
const lines = (s) => (s || "").split("\n").map((l) => l.trim()).filter(Boolean);
const CHANGED_EVENT = "scope-sets-changed";
const announce = () => window.dispatchEvent(new Event(CHANGED_EVENT));

/* Merge a set into the current scope/caveats/notice. mode = replace | append */
export function applyScopeSet(current, set, mode) {
  const out = { scope_of_works: current.scope_of_works || "", caveats: current.caveats || "", notice: current.notice || null };
  for (const f of ["scope_of_works", "caveats"]) {
    const incoming = (set[f] || "").trim();
    if (!incoming) continue;
    if (mode === "append" && out[f].trim()) {
      const have = new Set(lines(out[f]).map((l) => l.toLowerCase()));
      const extra = lines(incoming).filter((l) => !have.has(l.toLowerCase()));
      out[f] = extra.length ? `${out[f].trimEnd()}\n${extra.join("\n")}` : out[f];
    } else out[f] = incoming;
  }
  if (set.notice?.text) {
    const keepFlag = current.notice?.visible_to_client;
    if (mode === "replace" || !current.notice?.text) out.notice = { ...EMPTY_NOTICE, ...set.notice, visible_to_client: !!keepFlag };
  }
  return out;
}

const summary = (s) => [
  lines(s.scope_of_works).length ? `${lines(s.scope_of_works).length} scope line${lines(s.scope_of_works).length === 1 ? "" : "s"}` : null,
  lines(s.caveats).length ? `${lines(s.caveats).length} caveat${lines(s.caveats).length === 1 ? "" : "s"}` : null,
  s.notice?.text ? "safety notice" : null,
].filter(Boolean).join(" · ");

/* "Sets" button for a Scope box: apply a set in one tap (asks Replace / Add below when the box has text), save the current box as a set, manage. */
export default function ScopeSetPicker({ scope = "", caveats = "", notice = null, category = "general", onApply, testid = "scope-sets", compact = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [pending, setPending] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const load = () => API.get("/scope-sets").then(({ data }) => setData(data)).catch(() => setData({ sets: [], categories: [] }));
  useEffect(() => { if (open && !data) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onChanged = () => (open ? load() : setData(null));
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CHANGED_EVENT, onChanged);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasText = !!(scope.trim() || caveats.trim());
  const groups = useMemo(() => {
    const all = (data?.sets || []).filter((s) => !q || `${s.name} ${s.scope_of_works} ${s.caveats}`.toLowerCase().includes(q.toLowerCase()));
    const order = [category, "general", ...(data?.categories || []).filter((c) => c !== category && c !== "general")];
    return order.map((c) => ({ category: c, items: all.filter((s) => (s.category || "general") === c) })).filter((g) => g.items.length);
  }, [data, q, category]);

  const apply = (s, mode) => {
    onApply(applyScopeSet({ scope_of_works: scope, caveats, notice }, s, mode), s);
    API.post(`/scope-sets/${s.id}/used`).catch(() => {});
    toast.success(`"${s.name}" ${mode === "append" ? "added below" : "applied"}`);
    setPending(null);
    setOpen(false);
  };

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setQ(""); setPending(null); } }}>
        <PopoverTrigger asChild>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs shrink-0 border-violet-300 text-violet-800 hover:bg-violet-50 dark:border-violet-700 dark:text-violet-300 dark:hover:bg-violet-950/40" data-testid={`${testid}-btn`}>
            <Stack size={13} weight="fill" className="mr-1" /> {compact ? "Sets" : "Scope sets"}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[380px] p-0" data-testid={`${testid}-popover`}>
          <div className="p-2 border-b border-border flex items-center gap-2">
            <MagnifyingGlass size={14} className="text-muted-foreground shrink-0" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search scope sets…" className="flex-1 bg-transparent text-xs outline-none" data-testid={`${testid}-search`} />
            <Button type="button" size="sm" variant="ghost" className="h-6 px-1.5 text-[11px]" onClick={() => { setOpen(false); setManage(true); }} data-testid={`${testid}-manage-btn`}>
              <GearSix size={12} className="mr-1" /> Manage
            </Button>
          </div>
          <div className="max-h-[320px] overflow-y-auto p-1" data-testid={`${testid}-list`}>
            {!data && <p className="text-xs text-muted-foreground p-3">Loading…</p>}
            {data && groups.length === 0 && (
              <p className="text-xs text-muted-foreground p-3">{q ? "Nothing matches that search." : "No scope sets yet — fill in a scope and tap \"Save current as a set\"."}</p>
            )}
            {groups.map((g) => (
              <div key={g.category} className="mb-1">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold px-2 pt-2 pb-1">
                  {label(g.category)}{g.category === category && category !== "general" ? " · this trade" : ""}
                </p>
                {g.items.map((s) => (
                  <div key={s.id} data-testid={`${testid}-set-${s.id}`}>
                    <button type="button" onClick={() => (hasText ? setPending(pending === s.id ? null : s.id) : apply(s, "replace"))}
                            className={`w-full text-left text-xs px-2 py-1.5 rounded-md hover:bg-violet-50 dark:hover:bg-violet-950/30 ${pending === s.id ? "bg-violet-50 dark:bg-violet-950/30" : ""}`} data-testid={`${testid}-set-${s.id}-btn`}>
                      <span className="font-medium">{s.name}</span>
                      <span className="block text-[10px] text-muted-foreground">{summary(s)}</span>
                    </button>
                    {pending === s.id && (
                      <div className="mx-2 mb-1.5 rounded-md border border-violet-200 dark:border-violet-800 bg-violet-50/60 dark:bg-violet-950/20 p-2 flex items-center gap-2" data-testid={`${testid}-set-${s.id}-confirm`}>
                        <Warning size={13} className="text-violet-700 shrink-0" />
                        <span className="text-[11px] flex-1">This box already has text.</span>
                        <Button type="button" size="sm" className="h-6 text-[11px] px-2" onClick={() => apply(s, "replace")} data-testid={`${testid}-set-${s.id}-replace`}>Replace</Button>
                        <Button type="button" size="sm" variant="outline" className="h-6 text-[11px] px-2" onClick={() => apply(s, "append")} data-testid={`${testid}-set-${s.id}-append`}>Add below</Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="p-2 border-t border-border">
            <Button type="button" size="sm" variant="outline" className="w-full h-7 text-xs" disabled={!hasText && !notice?.text} onClick={() => { setOpen(false); setSaveOpen(true); }} data-testid={`${testid}-save-btn`}>
              <FloppyDisk size={13} className="mr-1" /> Save current as a set
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <ScopeSetDialog open={saveOpen} onOpenChange={setSaveOpen} initial={{ name: "", category, scope_of_works: scope, caveats, notice: notice?.text ? { tone: notice.tone, title: notice.title, text: notice.text } : null }} />
      <Dialog open={manage} onOpenChange={setManage}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="scope-sets-manage-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Scope sets</DialogTitle>
            <DialogDescription>Named bundles of scope, caveats and safety notice. Apply one from any Scope box in a single tap.</DialogDescription>
          </DialogHeader>
          <ScopeSetsPanel embedded />
        </DialogContent>
      </Dialog>
    </>
  );
}

/* Create / edit a set */
export function ScopeSetDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({ name: "", category: "general", scope_of_works: "", caveats: "", notice: null, ...(initial || {}) }); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: form.name, category: form.category, scope_of_works: form.scope_of_works, caveats: form.caveats, notice: form.notice?.text ? form.notice : null };
      const res = form.id ? await API.put(`/scope-sets/${form.id}`, payload) : await API.post("/scope-sets", payload);
      toast.success(form.id ? "Scope set updated" : `"${form.name}" saved — it's now one tap away in every Scope box`);
      announce();
      onSaved?.(res.data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="scope-set-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">{form.id ? "Edit scope set" : "Save as a scope set"}</DialogTitle>
          <DialogDescription>{form.id ? "Changes apply to future uses only." : "Name this scope + caveats + notice so it can be applied in one tap on any request, job, sub-job or quote."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_200px] gap-3">
            <div><Label className="text-xs">Name</Label><Input value={form.name || ""} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Kitchen tap replacement" className="mt-1 h-9 text-sm" data-testid="scope-set-name" autoFocus /></div>
            <div><Label className="text-xs">Trade</Label>
              <Select value={form.category || "general"} onValueChange={(v) => setForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="scope-set-category"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.keys(CATEGORY_LABELS).map((c) => <SelectItem key={c} value={c}>{CATEGORY_LABELS[c]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label className="text-xs">Scope of Works</Label><Textarea value={form.scope_of_works || ""} onChange={(e) => setForm((p) => ({ ...p, scope_of_works: e.target.value }))} rows={5} className="mt-1 text-xs" data-testid="scope-set-scope" /></div>
          <div><Label className="text-xs">Caveats & exclusions</Label><Textarea value={form.caveats || ""} onChange={(e) => setForm((p) => ({ ...p, caveats: e.target.value }))} rows={3} className="mt-1 text-xs" data-testid="scope-set-caveats" /></div>
          <div>
            <Label className="text-xs">Safety notice <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <div className="mt-1"><NoticeEditor value={form.notice || EMPTY_NOTICE} onChange={(n) => setForm((p) => ({ ...p, notice: n }))} testid="scope-set-notice" compact /></div>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="scope-set-cancel">Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy || !(form.name || "").trim()} data-testid="scope-set-save">{busy ? "Saving…" : form.id ? "Save changes" : "Save set"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* Management grid — used inside the picker's Manage dialog and on the Templates page */
export function ScopeSetsPanel({ embedded = false }) {
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = async () => { try { const { data } = await API.get("/scope-sets"); setData(data); } catch { setData({ sets: [] }); } };
  useEffect(() => { load(); }, []);
  useEffect(() => { window.addEventListener(CHANGED_EVENT, load); return () => window.removeEventListener(CHANGED_EVENT, load); }, []);
  const remove = async (s) => {
    if (!window.confirm(`Remove the scope set "${s.name}"?`)) return;
    try { await API.delete(`/scope-sets/${s.id}`); toast.success("Removed"); announce(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!data) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div data-testid="scope-sets-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-muted-foreground">{data.sets.length} set{data.sets.length === 1 ? "" : "s"}</p>
        <Button size="sm" onClick={() => setEditing({ name: "", category: "general", scope_of_works: "", caveats: "", notice: null })} data-testid="scope-sets-new-btn"><Plus size={14} className="mr-1" /> New scope set</Button>
      </div>
      {data.sets.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-border rounded-md">
          <Stack size={28} className="mx-auto text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">No scope sets yet</p>
          <p className="text-xs text-muted-foreground mt-1">Fill in a scope on any request, job or quote and use "Save current as a set" — or create one here.</p>
        </div>
      ) : (
        <div className={`grid gap-3 ${embedded ? "sm:grid-cols-2" : "md:grid-cols-2 xl:grid-cols-3"}`}>
          {data.sets.map((s) => (
            <div key={s.id} className="border border-border rounded-md p-3 hover:border-violet-300 transition-colors" data-testid={`scope-set-card-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <Badge variant="outline" className="text-[9px] mb-1">{label(s.category)}</Badge>
                  <p className="font-semibold text-sm truncate" data-testid={`scope-set-card-${s.id}-name`}>{s.name}</p>
                  <p className="text-[10px] text-muted-foreground">{summary(s)}{s.uses ? ` · used ${s.uses}×` : ""}</p>
                </div>
                <div className="flex gap-0.5 shrink-0">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(s)} data-testid={`scope-set-edit-${s.id}`}><PencilSimple size={14} /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-600" onClick={() => remove(s)} data-testid={`scope-set-delete-${s.id}`}><Trash size={14} /></Button>
                </div>
              </div>
              {s.scope_of_works && <p className="text-[11px] text-muted-foreground mt-2 whitespace-pre-wrap line-clamp-4">{s.scope_of_works}</p>}
            </div>
          ))}
        </div>
      )}
      <ScopeSetDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} initial={editing || {}} />
    </div>
  );
}
